import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getRepairParts } from "../../api/apiGetrequest";
import { UpdateRepairParts } from "../../api/apirequest";
import SparePartInput from "./PartsComponent";

const SparePartsModal = ({
  isOpen,
  fetchRepairs,
  setSpareParts,
  repair,
  onClose,
  spareParts = [],
  onUpdate,
}) => {
  // State for search functionality
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredParts, setFilteredParts] = useState(spareParts);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editData, setEditData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [newpo, setNewpo] = useState(false);

  const fetchRepairParts = async () => {
    try {
      const { result: parts } = await getRepairParts(repair?.RepairID);
      setFilteredParts(parts || []);
      setSpareParts(parts || []);
    } catch (error) {
      //   toast.error("Failed to fetch repair parts");
      console.error("Failed to fetch repair parts:", error);
    }
  };

  useEffect(() => {
    if (repair?.RepairID) {
      fetchRepairParts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repair?.RepairID]);

  // Effect to update filtered parts when search term or spare parts list changes
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredParts(spareParts);
    } else {
      const filtered = spareParts.filter(
        (part) =>
          part.SparePart?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          part.RepairPartID?.toString().includes(searchTerm)
      );
      setFilteredParts(filtered);
    }
  }, [searchTerm, spareParts]);

  // Handle modal click to prevent closing when clicking inside
  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  // Handle key press for ESC to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    // Prevent scrolling on the body when modal is open
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "auto";
    };
  }, [isOpen, onClose]);

  // Format date string to a more readable format
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  // Get status text based on StatusID
  const getStatusText = (statusId) => {
    const statuses = {
      1: "Active",
      2: "Pending",
      3: "Completed",
      4: "Cancelled",
      0: "Deleted",
      // Add more statuses as needed
    };
    return statuses[statusId] || "Unknown";
  };

  // Handle edit functions
  const handleEditClick = (part) => {
    setEditingIndex(part.RepairPartID);
    setEditData({
      SpareCost: part.SpareCost,
      Comment: part.Comment,
    });
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditData({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditData({
      ...editData,
      [name]: name === "SpareCost" ? parseFloat(value) || 0 : value,
    });
  };

  const handleSaveEdit = async (row, deleteFlag = false) => {
    setIsLoading(true);
    try {
      const result = await UpdateRepairParts({
        repairPartID: row.RepairPartID,
        repairID: row.RepairID,
        sparePart: row.SparePart,
        spareCost: editData.SpareCost || row.SpareCost,
        comment: editData.Comment || row.Comment,
        dateAdded: new Date(),
        statusID: deleteFlag ? 0 : row.StatusID,
      });

      if (result.success) {
        setEditingIndex(null);
        fetchRepairParts();
        toast.success(
          deleteFlag ? "Part deleted successfully" : "Updated successfully"
        );
        if (onUpdate) {
          onUpdate();
        }
      } else {
        toast.error(result.message || "Operation failed");
      }
    } catch (error) {
      toast.error(
        deleteFlag ? "Failed to delete part" : "Failed to update part"
      );
      console.error("Operation failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // If modal is not open, don't render anything
  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={handleModalClick}>
        <div style={styles.modalHeader}>
          <h2 style={styles.title}>Spare Parts List</h2>
          <button style={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        {/* <button onClick={() => setNewpo(!newpo)}>{newpo ? 'cancel' : 'Add new part'} </button> */}

        {newpo ? (
          <SparePartInput
            repair={repair}
            spareParts={spareParts}
            setSpareParts={setSpareParts}
          />
        ) : (
          <div style={styles.modalContent}>
            {filteredParts.length > 0 ? (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.tableHeader}>Spare Part</th>
                    <th style={styles.tableHeader}>Cost</th>
                    <th style={styles.tableHeader}>Date Added</th>
                    <th style={styles.tableHeader}>Status</th>
                    <th style={styles.tableHeader}>Comment</th>
                    <th style={styles.tableHeader}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredParts.map((part) => (
                    <tr key={part.RepairPartID} style={styles.tableRow}>
                      <td style={styles.tableCell}>{part.SparePart}</td>

                      <td style={styles.tableCell}>
                        {editingIndex === part.RepairPartID ? (
                          <input
                            type="number"
                            name="SpareCost"
                            value={editData.SpareCost}
                            onChange={handleInputChange}
                            style={styles.editInput}
                          />
                        ) : (
                          `KES. ${part?.SpareCost?.toLocaleString()}`
                        )}
                      </td>

                      <td style={styles.tableCell}>
                        {formatDate(part.DateAdded)}
                      </td>

                      <td style={styles.tableCell}>
                        <span
                          style={{
                            ...styles.statusBadge,
                            backgroundColor:
                              part.StatusID === 1
                                ? "#4CAF50"
                                : part.StatusID === 2
                                ? "#FFC107"
                                : part.StatusID === 3
                                ? "#2196F3"
                                : part.StatusID === 0
                                ? "#F44336"
                                : "#F44336",
                          }}
                        >
                          {getStatusText(part.StatusID)}
                        </span>
                      </td>

                      <td style={styles.tableCell}>
                        {editingIndex === part.RepairPartID ? (
                          <input
                            type="text"
                            name="Comment"
                            value={editData.Comment}
                            onChange={handleInputChange}
                            style={styles.editInput}
                          />
                        ) : (
                          part.Comment || "-"
                        )}
                      </td>

                      <td style={styles.tableCell}>
                        {editingIndex === part.RepairPartID ? (
                          <div style={styles.actionButtons}>
                            <button
                              style={styles.saveButton}
                              onClick={() => handleSaveEdit(part)}
                              disabled={isLoading}
                            >
                              {isLoading ? "Saving..." : "Save"}
                            </button>
                            <button
                              style={styles.cancelButton}
                              onClick={handleCancelEdit}
                              disabled={isLoading}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div style={styles.actionButtons}>
                            <button
                              style={styles.editButton}
                              onClick={() => handleEditClick(part)}
                            >
                              Edit
                            </button>
                            <button
                              style={styles.deleteButton}
                              onClick={() => {
                                if (
                                  window.confirm(
                                    "Are you sure you want to delete this part?"
                                  )
                                ) {
                                  handleSaveEdit(part, true);
                                }
                              }}
                              disabled={isLoading}
                            >
                              {isLoading && editingIndex === part.RepairPartID
                                ? "Deleting..."
                                : "Delete"}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={styles.noResults}>
                No spare parts found matching "{searchTerm}"
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Enhanced styles with additional button and input styling
const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modal: {
    backgroundColor: "#fff",
    borderRadius: "6px",
    boxShadow: "0 5px 15px rgba(0, 0, 0, 0.3)",
    width: "90%",
    maxWidth: "900px",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "15px 20px",
    borderBottom: "1px solid #eee",
  },
  title: {
    margin: 0,
    color: "#333",
    fontSize: "20px",
    fontWeight: 600,
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "24px",
    color: "#666",
    cursor: "pointer",
    outline: "none",
    padding: "0 5px",
  },
  searchContainer: {
    padding: "15px 20px",
    borderBottom: "1px solid #eee",
  },
  searchInput: {
    width: "100%",
    padding: "10px 15px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "14px",
    outline: "none",
  },
  modalContent: {
    padding: "15px 20px",
    overflowY: "auto",
    overflowX: "auto",
    maxHeight: "calc(90vh - 140px)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "800px",
  },
  tableHeader: {
    textAlign: "left",
    padding: "10px 15px",
    backgroundColor: "#f5f5f5",
    borderBottom: "1px solid #ddd",
    color: "#333",
    whiteSpace: "nowrap",
  },
  tableRow: {
    borderBottom: "1px solid #eee",
  },
  tableCell: {
    padding: "12px 15px",
    whiteSpace: "nowrap",
  },
  noResults: {
    padding: "20px",
    textAlign: "center",
    color: "#666",
  },
  statusBadge: {
    padding: "4px 8px",
    borderRadius: "12px",
    color: "white",
    fontSize: "12px",
    fontWeight: "bold",
    display: "inline-block",
  },
  editInput: {
    padding: "8px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    width: "100%",
    fontSize: "14px",
  },
  actionButtons: {
    display: "flex",
    gap: "5px",
  },
  editButton: {
    backgroundColor: "#2196F3",
    color: "white",
    border: "none",
    padding: "6px 10px",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "13px",
  },
  deleteButton: {
    backgroundColor: "#F44336",
    color: "white",
    border: "none",
    padding: "6px 10px",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "13px",
  },
  saveButton: {
    backgroundColor: "#4CAF50",
    color: "white",
    border: "none",
    padding: "6px 10px",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "13px",
  },
  cancelButton: {
    backgroundColor: "#9E9E9E",
    color: "white",
    border: "none",
    padding: "6px 10px",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "13px",
  },
};

export default SparePartsModal;
